import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Radio,
  Stack,
  Typography,
} from '@mui/material';

export interface IdentityCandidate {
  patientId: string;
  name: string | null;
  dob: string | null;
  email: string | null;
  phone: string | null;
  /** Match confidence, 0-100. */
  confidence: number;
}

interface IdentityDecisionDialogProps {
  open: boolean;
  /** The booker's name, for the title. */
  requesterName: string;
  candidates: IdentityCandidate[];
  /** Subject-language singular, e.g. "patient" / "client". */
  subjectSingular: string;
  isSaving?: boolean;
  onClose: () => void;
  /** null = create a new record; otherwise link to that patient. */
  onDecide: (patientId: string | null) => void;
}

/** Link-or-create decision, shown only when accepting a booking finds
 * possible existing-patient matches (ADR-0076: a fuzzy match is never
 * auto-merged). Candidates and "create new" are equal options; create-new is
 * preselected as the no-risk default — a wrong link puts someone's booking
 * in another person's chart, a duplicate record is merely untidy. */
export function IdentityDecisionDialog({
  open,
  requesterName,
  candidates,
  subjectSingular,
  isSaving = false,
  onClose,
  onDecide,
}: IdentityDecisionDialogProps) {
  const [choice, setChoice] = useState<string | null>(null);

  const optionSx = (selected: boolean) => ({
    p: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    cursor: 'pointer',
    borderColor: selected ? 'primary.main' : 'divider',
    bgcolor: selected ? 'action.hover' : 'background.paper',
  });

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Confirm {requesterName}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          We found similar {subjectSingular} records. Book this into an
          existing record, or create a new one.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          {candidates.map(candidate => (
            <Paper
              key={candidate.patientId}
              variant="outlined"
              onClick={() => setChoice(candidate.patientId)}
              sx={optionSx(choice === candidate.patientId)}
            >
              <Radio
                checked={choice === candidate.patientId}
                size="small"
                sx={{ p: 0.5 }}
                slotProps={{
                  input: {
                    'aria-label': `Link to ${candidate.name ?? 'existing record'}`,
                  },
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2">
                  {candidate.name ?? 'Unnamed record'}
                  {candidate.dob && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      DOB {candidate.dob}
                    </Typography>
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {[candidate.email, candidate.phone].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  flexShrink: 0,
                  color:
                    candidate.confidence >= 80 ? 'success.main' : 'text.disabled',
                  fontWeight: 600,
                }}
              >
                {candidate.confidence}%
              </Typography>
            </Paper>
          ))}
          <Paper
            variant="outlined"
            onClick={() => setChoice(null)}
            sx={optionSx(choice === null)}
          >
            <Radio
              checked={choice === null}
              size="small"
              sx={{ p: 0.5 }}
              slotProps={{ input: { 'aria-label': 'Create a new record' } }}
            />
            <Box>
              <Typography variant="subtitle2">
                None of these — new {subjectSingular}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Creates a record from their booking details
              </Typography>
            </Box>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
          onClick={() => onDecide(choice)}
        >
          Confirm booking
        </Button>
      </DialogActions>
    </Dialog>
  );
}
